import { Injectable, signal, computed } from '@angular/core';

/**
 * Toast taxonomy — five v1.1 variants per `design-notes.md` / GAP-FU-02.
 *
 * - `success`  — operation completed (green / bb-accent-green)
 * - `info`     — informational, no action expected (blue / bb-accent-blue)
 * - `warning`  — caution, recoverable (amber / bb-accent-amber)
 * - `danger`   — destructive failure / rejection (red / bb-accent-red)
 * - `neutral`  — chrome-only banner (slate / bb-text-secondary)
 *
 * `error` is a documented alias for `danger` retained for backwards
 * compatibility with the (very large) set of existing call-sites that
 * call `toast.error(...)` or pass `severity: 'error'`. Both render
 * identically; new code should prefer `danger`.
 */
export type ToastType = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'error';
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

/**
 * Optional inline action surfaced next to the toast body
 * (e.g. "Undo", "Retry", "View"). Kept simple — the callback runs in
 * the caller's injection context.
 */
export interface ToastAction {
  /** Visible label for the action button. */
  label: string;
  /** Invoked when the user clicks the action; toast auto-dismisses afterwards. */
  callback: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
  sticky?: boolean;
  /** GAP-FU-02 · optional inline action button. */
  action?: ToastAction;
}

export interface ToastOptions {
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
  sticky?: boolean;
  /** GAP-FU-02 · optional inline action button. */
  action?: ToastAction;
}

/**
 * Normalise the legacy `error` alias to its v1.1 canonical name `danger`
 * so downstream rendering and dedup keying are uniform.
 */
const normaliseType = (type: ToastType): Exclude<ToastType, 'error'> =>
  type === 'error' ? 'danger' : type;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private _toasts = signal<Toast[]>([]);

  readonly toasts = computed(() => this._toasts());

  private defaultDuration = 5000;
  private maxVisibleToasts = 3;
  private deduplicationWindowMs = 2000; // Ignore duplicate messages within this window
  private recentMessages = new Map<string, number>(); // message hash -> timestamp

  /**
   * Show a success toast
   * Supports both new API (ToastOptions) and legacy API (title, detail, life)
   */
  success(options: ToastOptions | string): void;
  success(title: string, detail: string, life?: number): void;
  success(optionsOrTitle: ToastOptions | string, detail?: string, life?: number): void {
    if (detail !== undefined && typeof optionsOrTitle === 'string') {
      this.show('success', { title: optionsOrTitle, message: detail, duration: life });
    } else {
      this.show('success', optionsOrTitle as ToastOptions | string);
    }
  }

  /**
   * Show a danger toast (v1.1 canonical name; `error()` is the alias).
   * Supports both new API (ToastOptions) and legacy API (title, detail, life)
   */
  danger(options: ToastOptions | string): void;
  danger(title: string, detail: string, life?: number): void;
  danger(optionsOrTitle: ToastOptions | string, detail?: string, life?: number): void {
    if (detail !== undefined && typeof optionsOrTitle === 'string') {
      this.show('danger', { title: optionsOrTitle, message: detail, duration: life });
    } else {
      this.show('danger', optionsOrTitle as ToastOptions | string);
    }
  }

  /**
   * Show an error toast — backwards-compatible alias for `danger()`.
   * Supports both new API (ToastOptions) and legacy API (title, detail, life)
   */
  error(options: ToastOptions | string): void;
  error(title: string, detail: string, life?: number): void;
  error(optionsOrTitle: ToastOptions | string, detail?: string, life?: number): void {
    if (detail !== undefined && typeof optionsOrTitle === 'string') {
      this.show('danger', { title: optionsOrTitle, message: detail, duration: life });
    } else {
      this.show('danger', optionsOrTitle as ToastOptions | string);
    }
  }

  /**
   * Show a neutral chrome-only banner (v1.1 GAP-FU-02 addition).
   */
  neutral(options: ToastOptions | string): void {
    this.show('neutral', options);
  }

  /**
   * Show a warning toast
   */
  warning(options: ToastOptions | string): void {
    this.show('warning', options);
  }

  /**
   * Alias for warning() - backwards compatibility
   * Accepts (title, message, life) format for convenience
   */
  warn(title: string, message?: string, life?: number): void {
    this.show('warning', { title, message, duration: life });
  }

  /**
   * Show an info toast
   * Supports both new API (ToastOptions) and legacy API (title, detail, life)
   */
  info(options: ToastOptions | string): void;
  info(title: string, detail: string, life?: number): void;
  info(optionsOrTitle: ToastOptions | string, detail?: string, life?: number): void {
    if (detail !== undefined && typeof optionsOrTitle === 'string') {
      this.show('info', { title: optionsOrTitle, message: detail, duration: life });
    } else {
      this.show('info', optionsOrTitle as ToastOptions | string);
    }
  }

  /**
   * Show a toast of specified type
   */
  show(type: ToastType, options: ToastOptions | string): void {
    const toastOptions: ToastOptions = typeof options === 'string'
      ? { title: options }
      : options;

    // Normalise legacy `error` alias to canonical `danger` so dedup
    // keys + rendering paths stay uniform.
    const canonicalType = normaliseType(type);

    // Deduplication: Check if same message was shown recently
    const messageHash = this.getMessageHash(canonicalType, toastOptions.title, toastOptions.message);
    const now = Date.now();

    if (this.isDuplicateMessage(messageHash, now)) {
      return; // Skip duplicate message
    }

    // Record this message for deduplication
    this.recentMessages.set(messageHash, now);
    this.cleanupOldMessages(now);

    const toast: Toast = {
      id: this.generateId(),
      type: canonicalType,
      title: toastOptions.title,
      message: toastOptions.message,
      duration: toastOptions.duration ?? this.defaultDuration,
      dismissible: toastOptions.dismissible ?? true,
      sticky: toastOptions.sticky ?? false,
      action: toastOptions.action,
    };

    this._toasts.update(toasts => {
      const newToasts = [...toasts, toast];
      // Limit to maxVisibleToasts - remove oldest if exceeding limit
      if (newToasts.length > this.maxVisibleToasts) {
        return newToasts.slice(-this.maxVisibleToasts);
      }
      return newToasts;
    });

    // Auto-dismiss if duration is set
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => this.dismiss(toast.id), toast.duration);
    }
  }

  /**
   * Dismiss a specific toast
   */
  dismiss(id: string): void {
    this._toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this._toasts.set([]);
  }

  /**
   * Dismiss only non-sticky toasts.
   * Sticky toasts (duration: 0 or sticky: true) are preserved.
   * Used on route changes to clear transient notifications while keeping important ones.
   */
  dismissNonSticky(): void {
    this._toasts.update(toasts => toasts.filter(t => t.sticky || t.duration === 0));
  }

  /**
   * ToastService compatibility: add a message
   */
  add(options: {
    severity?: 'success' | 'info' | 'warn' | 'error' | 'warning';
    summary?: string;
    detail?: string;
    life?: number;
    sticky?: boolean;
    closable?: boolean;
    key?: string;
  }): void {
    // Map legacy library 'warn' to our 'warning' type
    let type: ToastType;
    if (options.severity === 'warn' || options.severity === 'warning') {
      type = 'warning';
    } else {
      type = (options.severity as ToastType) || 'info';
    }

    const isSticky = options.sticky ?? false;
    const duration = isSticky ? 0 : (options.life ?? this.defaultDuration);

    this.show(type, {
      title: options.summary || '',
      message: options.detail,
      duration,
      dismissible: options.closable !== false,
      sticky: isSticky
    });
  }

  /**
   * ToastService compatibility: clear all messages
   */
  clear(): void {
    this.dismissAll();
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Generate a hash for deduplication based on type, title, and message.
   * Uses the canonical (non-aliased) type so `error` and `danger` share
   * a dedup window — emitting both within the dedup window produces one toast.
   */
  private getMessageHash(type: Exclude<ToastType, 'error'>, title: string, message?: string): string {
    return `${type}:${title}:${message || ''}`;
  }

  /**
   * Check if a message was shown recently (within deduplication window)
   */
  private isDuplicateMessage(hash: string, now: number): boolean {
    const lastShown = this.recentMessages.get(hash);
    if (lastShown && now - lastShown < this.deduplicationWindowMs) {
      return true;
    }
    return false;
  }

  /**
   * Clean up old message entries from deduplication map
   */
  private cleanupOldMessages(now: number): void {
    // Remove entries older than deduplication window
    for (const [hash, timestamp] of this.recentMessages.entries()) {
      if (now - timestamp > this.deduplicationWindowMs) {
        this.recentMessages.delete(hash);
      }
    }
  }
}
